import { useEffect, useMemo, useState } from "react";
import { Calendar, dateFnsLocalizer } from "react-big-calendar";
import { format, parse, startOfWeek, getDay, addMinutes, addDays } from "date-fns";
import enUS from "date-fns/locale/en-US";
import "react-big-calendar/lib/css/react-big-calendar.css";
import { supabase } from "../supabaseClient";
import Modal from "../components/Modal";
import {
  Calendar as CalendarIcon,
  MapPin,
  Clock,
  AlertCircle,
  Info,
} from "lucide-react";

const locales = { "en-US": enUS };
const localizer = dateFnsLocalizer({ format, parse, startOfWeek, getDay, locales });

const weekDays = [
  { label: "Sunday", value: 0 },
  { label: "Monday", value: 1 },
  { label: "Tuesday", value: 2 },
  { label: "Wednesday", value: 3 },
  { label: "Thursday", value: 4 },
  { label: "Friday", value: 5 },
  { label: "Saturday", value: 6 },
];

const buildDayState = (override = {}) => ({
  enabled: false,
  start: "09:00",
  end: "17:00",
  location: "",
  slotDuration: 60,
  requiresApproval: false,
  ...override,
});

const toDateKey = (date) => format(date, "yyyy-MM-dd");

const buildDateRange = (daysAhead = 60, daysBack = 7) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const rangeStart = addDays(today, -daysBack);
  const rangeEnd = addDays(today, daysAhead);
  return {
    rangeStart,
    rangeEnd,
    startStr: format(rangeStart, "yyyy-MM-dd"),
    endStr: format(rangeEnd, "yyyy-MM-dd"),
  };
};

const combineDateTime = (date, timeStr) => {
  const [hours, minutes] = timeStr.split(":").map((v) => Number(v));
  const dt = new Date(date);
  dt.setHours(hours || 0, minutes || 0, 0, 0);
  return dt;
};

const generateSlots = ({
  availabilityRows,
  exceptionRows,
  bookings,
  startDate,
  days,
  defaultLocation,
}) => {
  const slots = [];
  const bookingMap = new Map();
  bookings.forEach((b) => {
    bookingMap.set(new Date(b.start_at).getTime(), b);
  });

  for (let i = 0; i < days; i += 1) {
    const day = addDays(startDate, i);
    const dateKey = toDateKey(day);
    const exception = exceptionRows.find((row) => row.exception_date === dateKey);

    if (exception?.is_unavailable) continue;

    const weekday = day.getDay();
    const availability = availabilityRows.find((row) => row.weekday === weekday);

    const startTime = exception?.start_time || availability?.start_time;
    const endTime = exception?.end_time || availability?.end_time;
    if (!startTime || !endTime) continue;

    const slotDuration =
      exception?.slot_duration_minutes || availability?.slot_duration_minutes || 60;
    const requiresApproval =
      typeof exception?.requires_approval === "boolean"
        ? exception.requires_approval
        : availability?.requires_approval || false;
    const location =
      exception?.location_address || availability?.location_address || defaultLocation || "";

    let slotStart = combineDateTime(day, startTime);
    const slotEndBoundary = combineDateTime(day, endTime);

    while (slotStart < slotEndBoundary) {
      const slotEnd = addMinutes(slotStart, slotDuration);
      if (slotEnd > slotEndBoundary) break;

      const booking = bookingMap.get(slotStart.getTime());
      if (!booking || (booking.status !== "rejected" && booking.status !== "cancelled")) {
        slots.push({
          id: `${dateKey}-${slotStart.toISOString()}`,
          title: booking
            ? booking.status === "pending"
              ? "Pending"
              : "Booked"
            : "Available",
          start: new Date(slotStart),
          end: new Date(slotEnd),
          kind: booking ? "booked" : "available",
          status: booking?.status || "available",
          requiresApproval: booking?.requires_approval ?? requiresApproval,
          location,
          bookingId: booking?.id || null,
        });
      }

      slotStart = slotEnd;
    }
  }

  return slots;
};

function TrainerScheduleView({ user }) {
  const [program, setProgram] = useState(null);
  const [availability, setAvailability] = useState(() =>
    weekDays.reduce((acc, day) => {
      acc[day.value] = buildDayState();
      return acc;
    }, {})
  );
  const [existingAvailability, setExistingAvailability] = useState([]);
  const [exceptions, setExceptions] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [exceptionForm, setExceptionForm] = useState({
    date: "",
    isUnavailable: false,
    start: "",
    end: "",
    location: "",
    slotDuration: "",
    requiresApproval: false,
  });

  useEffect(() => {
    if (!user?.id) return;
    const load = async () => {
      const { data: programData } = await supabase
        .from("programs")
        .select("id, name, location_address")
        .eq("administrator", user.id)
        .maybeSingle();

      if (programData) setProgram(programData);
    };
    load();
  }, [user]);

  useEffect(() => {
    if (!program?.id) return;

    const load = async () => {
      const { rangeStart, rangeEnd, startStr, endStr } = buildDateRange(90, 7);
      const [availabilityRes, exceptionRes, bookingRes] = await Promise.all([
        supabase
          .from("trainer_availability")
          .select(
            "id, weekday, start_time, end_time, location_address, slot_duration_minutes, requires_approval"
          )
          .eq("program_id", program.id),
        supabase
          .from("trainer_availability_exceptions")
          .select(
            "id, exception_date, is_unavailable, start_time, end_time, location_address, slot_duration_minutes, requires_approval"
          )
          .eq("program_id", program.id)
          .gte("exception_date", startStr)
          .lte("exception_date", endStr)
          .order("exception_date", { ascending: true }),
        supabase
          .from("trainer_bookings")
          .select(
            "id, start_at, end_at, status, location_address, requires_approval, users(full_name)"
          )
          .eq("program_id", program.id)
          .gte("start_at", rangeStart.toISOString())
          .lte("start_at", rangeEnd.toISOString())
          .order("start_at", { ascending: true }),
      ]);

      setExistingAvailability(availabilityRes.data || []);
      setExceptions(exceptionRes.data || []);
      setBookings(bookingRes.data || []);

      if (availabilityRes.data?.length) {
        const nextAvailability = weekDays.reduce((acc, day) => {
          const row = availabilityRes.data.find((r) => r.weekday === day.value);
          if (row) {
            acc[day.value] = buildDayState({
              enabled: true,
              start: row.start_time,
              end: row.end_time,
              location: row.location_address || "",
              slotDuration: row.slot_duration_minutes || 60,
              requiresApproval: row.requires_approval || false,
            });
          } else {
            acc[day.value] = buildDayState();
          }
          return acc;
        }, {});
        setAvailability(nextAvailability);
      }
    };

    load();
  }, [program]);

  const handleAvailabilityChange = (weekday, field, value) => {
    setAvailability((prev) => ({
      ...prev,
      [weekday]: {
        ...prev[weekday],
        [field]: value,
      },
    }));
  };

  const saveSchedule = async () => {
    if (!program?.id) return;
    setSaving(true);
    setError("");

    try {
      const upserts = [];
      const deletes = [];

      weekDays.forEach((day) => {
        const state = availability[day.value];
        const existing = existingAvailability.find((row) => row.weekday === day.value);

        if (state.enabled && state.start && state.end) {
          upserts.push({
            program_id: program.id,
            weekday: day.value,
            start_time: state.start,
            end_time: state.end,
            location_address: state.location || null,
            slot_duration_minutes: Number(state.slotDuration) || 60,
            requires_approval: !!state.requiresApproval,
          });
        } else if (existing) {
          deletes.push(existing.id);
        }
      });

      if (upserts.length > 0) {
        const { error: upsertError } = await supabase
          .from("trainer_availability")
          .upsert(upserts, { onConflict: "program_id,weekday" });
        if (upsertError) throw upsertError;
      }

      if (deletes.length > 0) {
        const { error: deleteError } = await supabase
          .from("trainer_availability")
          .delete()
          .in("id", deletes);
        if (deleteError) throw deleteError;
      }

      const { data: availabilityRows } = await supabase
        .from("trainer_availability")
        .select(
          "id, weekday, start_time, end_time, location_address, slot_duration_minutes, requires_approval"
        )
        .eq("program_id", program.id);
      setExistingAvailability(availabilityRows || []);
    } catch (err) {
      console.error("Save schedule error", err);
      setError(err.message || "Unable to save schedule.");
    } finally {
      setSaving(false);
    }
  };

  const addException = async () => {
    if (!program?.id || !exceptionForm.date) return;

    try {
      const payload = {
        program_id: program.id,
        exception_date: exceptionForm.date,
        is_unavailable: exceptionForm.isUnavailable,
        start_time: exceptionForm.isUnavailable ? null : exceptionForm.start || null,
        end_time: exceptionForm.isUnavailable ? null : exceptionForm.end || null,
        location_address: exceptionForm.location || null,
        slot_duration_minutes: exceptionForm.slotDuration
          ? Number(exceptionForm.slotDuration)
          : null,
        requires_approval: exceptionForm.requiresApproval,
      };

      const { error: insertError } = await supabase
        .from("trainer_availability_exceptions")
        .insert(payload);
      if (insertError) throw insertError;

      const { startStr, endStr } = buildDateRange(90, 7);
      const { data: exceptionRows } = await supabase
        .from("trainer_availability_exceptions")
        .select(
          "id, exception_date, is_unavailable, start_time, end_time, location_address, slot_duration_minutes, requires_approval"
        )
        .eq("program_id", program.id)
        .gte("exception_date", startStr)
        .lte("exception_date", endStr)
        .order("exception_date", { ascending: true });
      setExceptions(exceptionRows || []);

      setExceptionForm({
        date: "",
        isUnavailable: false,
        start: "",
        end: "",
        location: "",
        slotDuration: "",
        requiresApproval: false,
      });
    } catch (err) {
      console.error("Exception error", err);
      setError(err.message || "Unable to add exception.");
    }
  };

  const removeException = async (id) => {
    try {
      const { error: deleteError } = await supabase
        .from("trainer_availability_exceptions")
        .delete()
        .eq("id", id);
      if (deleteError) throw deleteError;

      setExceptions((prev) => prev.filter((row) => row.id !== id));
    } catch (err) {
      console.error("Remove exception error", err);
      setError(err.message || "Unable to remove exception.");
    }
  };

  const updateBookingStatus = async (bookingId, status) => {
    try {
      const { error: updateError } = await supabase
        .from("trainer_bookings")
        .update({ status })
        .eq("id", bookingId);
      if (updateError) throw updateError;

      setBookings((prev) =>
        prev.map((b) => (b.id === bookingId ? { ...b, status } : b))
      );
    } catch (err) {
      console.error("Booking update error", err);
      setError(err.message || "Unable to update booking.");
    }
  };

  if (!program) {
    return (
      <div className="bg-white p-6 rounded-2xl border shadow-sm">
        <p className="text-sm text-gray-500">Create a trainer profile to set your schedule.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-xl flex items-center gap-2">
          <AlertCircle size={16} />
          <span className="text-sm font-bold">{error}</span>
        </div>
      )}

      <div className="bg-white p-6 rounded-3xl shadow-sm border border-gray-100">
        <div className="flex items-center gap-2 mb-4">
          <Clock className="text-blue-500" size={20} />
          <h2 className="text-lg font-bold text-gray-900">Weekly Working Hours</h2>
          <span className="text-xs text-gray-400 font-semibold flex items-center gap-1">
            <Info size={14} className="text-gray-400" />
            <span className="hidden sm:inline">Checked = working, unchecked = off</span>
          </span>
        </div>
        <div className="hidden md:grid md:grid-cols-6 gap-3 text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-2 px-1">
          <span>Day</span>
          <span>Start</span>
          <span>End</span>
          <span>Location</span>
          <span>Slot (min)</span>
          <span>Approval</span>
        </div>
        <div className="space-y-4">
          {weekDays.map((day) => {
            const dayState = availability[day.value];
            return (
              <div key={day.value} className="grid grid-cols-1 md:grid-cols-6 gap-3 items-center">
                <div className="md:col-span-1 flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={dayState.enabled}
                    onChange={(e) => handleAvailabilityChange(day.value, "enabled", e.target.checked)}
                    className="w-4 h-4 text-blue-600 rounded border-gray-300"
                  />
                  <span className="text-sm font-bold text-gray-700">{day.label}</span>
                </div>
                <div className="md:hidden text-[10px] font-bold uppercase tracking-wider text-gray-400">
                  Start
                </div>
                <input
                  type="time"
                  value={dayState.start}
                  onChange={(e) => handleAvailabilityChange(day.value, "start", e.target.value)}
                  disabled={!dayState.enabled}
                  className="bg-gray-50 border border-gray-200 text-sm rounded-xl px-3 py-2 font-semibold"
                />
                <div className="md:hidden text-[10px] font-bold uppercase tracking-wider text-gray-400">
                  End
                </div>
                <input
                  type="time"
                  value={dayState.end}
                  onChange={(e) => handleAvailabilityChange(day.value, "end", e.target.value)}
                  disabled={!dayState.enabled}
                  className="bg-gray-50 border border-gray-200 text-sm rounded-xl px-3 py-2 font-semibold"
                />
                <div className="md:hidden text-[10px] font-bold uppercase tracking-wider text-gray-400">
                  Location
                </div>
                <input
                  type="text"
                  value={dayState.location}
                  onChange={(e) => handleAvailabilityChange(day.value, "location", e.target.value)}
                  disabled={!dayState.enabled}
                  placeholder={program.location_address || "Location"}
                  className="bg-gray-50 border border-gray-200 text-sm rounded-xl px-3 py-2 font-semibold"
                />
                <div className="md:hidden text-[10px] font-bold uppercase tracking-wider text-gray-400">
                  Slot (min)
                </div>
                <input
                  type="number"
                  min="15"
                  step="15"
                  value={dayState.slotDuration}
                  onChange={(e) => handleAvailabilityChange(day.value, "slotDuration", e.target.value)}
                  disabled={!dayState.enabled}
                  className="bg-gray-50 border border-gray-200 text-sm rounded-xl px-3 py-2 font-semibold"
                />
                <div className="md:hidden text-[10px] font-bold uppercase tracking-wider text-gray-400">
                  Approval
                </div>
                <label className="flex items-center gap-2 text-sm font-semibold text-gray-600">
                  <input
                    type="checkbox"
                    checked={dayState.requiresApproval}
                    onChange={(e) => handleAvailabilityChange(day.value, "requiresApproval", e.target.checked)}
                    disabled={!dayState.enabled}
                    className="w-4 h-4 text-blue-600 rounded border-gray-300"
                  />
                  Needs approval
                </label>
              </div>
            );
          })}
        </div>
        <div className="mt-6 flex justify-end">
          <button
            type="button"
            onClick={saveSchedule}
            disabled={saving}
            className="px-5 py-2.5 rounded-xl bg-blue-600 text-white font-bold hover:bg-blue-700 transition disabled:opacity-70"
          >
            {saving ? "Saving..." : "Save Schedule"}
          </button>
        </div>
      </div>

      <div className="bg-white p-6 rounded-3xl shadow-sm border border-gray-100">
        <div className="flex items-center gap-2 mb-4">
          <CalendarIcon className="text-blue-500" size={20} />
          <h2 className="text-lg font-bold text-gray-900">Exceptions</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-6 gap-3 items-end">
          <div className="md:col-span-1">
            <label className="text-xs font-bold text-gray-500 uppercase">Date</label>
            <input
              type="date"
              value={exceptionForm.date}
              onChange={(e) => setExceptionForm((prev) => ({ ...prev, date: e.target.value }))}
              className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 text-sm font-semibold"
            />
          </div>
          <div className="md:col-span-1">
            <label className="text-xs font-bold text-gray-500 uppercase">Start</label>
            <input
              type="time"
              value={exceptionForm.start}
              onChange={(e) => setExceptionForm((prev) => ({ ...prev, start: e.target.value }))}
              disabled={exceptionForm.isUnavailable}
              className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 text-sm font-semibold"
            />
          </div>
          <div className="md:col-span-1">
            <label className="text-xs font-bold text-gray-500 uppercase">End</label>
            <input
              type="time"
              value={exceptionForm.end}
              onChange={(e) => setExceptionForm((prev) => ({ ...prev, end: e.target.value }))}
              disabled={exceptionForm.isUnavailable}
              className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 text-sm font-semibold"
            />
          </div>
          <div className="md:col-span-1">
            <label className="text-xs font-bold text-gray-500 uppercase">Location</label>
            <input
              type="text"
              value={exceptionForm.location}
              onChange={(e) => setExceptionForm((prev) => ({ ...prev, location: e.target.value }))}
              className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 text-sm font-semibold"
            />
          </div>
          <div className="md:col-span-1">
            <label className="text-xs font-bold text-gray-500 uppercase">Slot (min)</label>
            <input
              type="number"
              min="15"
              step="15"
              value={exceptionForm.slotDuration}
              onChange={(e) => setExceptionForm((prev) => ({ ...prev, slotDuration: e.target.value }))}
              className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 text-sm font-semibold"
            />
          </div>
          <div className="md:col-span-1 flex flex-col gap-2">
            <label className="flex items-center gap-2 text-sm font-semibold text-gray-600">
              <input
                type="checkbox"
                checked={exceptionForm.isUnavailable}
                onChange={(e) => setExceptionForm((prev) => ({ ...prev, isUnavailable: e.target.checked }))}
                className="w-4 h-4 text-blue-600 rounded border-gray-300"
              />
              Unavailable
            </label>
            <label className="flex items-center gap-2 text-sm font-semibold text-gray-600">
              <input
                type="checkbox"
                checked={exceptionForm.requiresApproval}
                onChange={(e) => setExceptionForm((prev) => ({ ...prev, requiresApproval: e.target.checked }))}
                className="w-4 h-4 text-blue-600 rounded border-gray-300"
              />
              Needs approval
            </label>
          </div>
        </div>
        <div className="mt-4 flex justify-end">
          <button
            type="button"
            onClick={addException}
            className="px-4 py-2 rounded-xl bg-gray-900 text-white font-bold hover:bg-gray-800 transition"
          >
            Add Exception
          </button>
        </div>

        <div className="mt-4 space-y-2">
          {exceptions.length === 0 ? (
            <p className="text-sm text-gray-500">No exceptions yet.</p>
          ) : (
            exceptions.map((row) => (
              <div
                key={row.id}
                className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-xl px-3 py-2"
              >
                <div>
                  <p className="text-sm font-bold text-gray-800">{row.exception_date}</p>
                  <p className="text-xs text-gray-500">
                    {row.is_unavailable
                      ? "Unavailable"
                      : `${row.start_time || ""} - ${row.end_time || ""}`}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => removeException(row.id)}
                  className="text-xs font-bold text-red-500 hover:text-red-700"
                >
                  Remove
                </button>
              </div>
            ))
          )}
        </div>
      </div>

      <div className="bg-white p-6 rounded-3xl shadow-sm border border-gray-100">
        <div className="flex items-center gap-2 mb-4">
          <CalendarIcon className="text-blue-500" size={20} />
          <h2 className="text-lg font-bold text-gray-900">Bookings</h2>
        </div>
        <div className="space-y-3">
          {bookings.length === 0 ? (
            <p className="text-sm text-gray-500">No bookings yet.</p>
          ) : (
            bookings.map((booking) => (
              <div
                key={booking.id}
                className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 bg-gray-50 border border-gray-200 rounded-xl px-4 py-3"
              >
                <div>
                  <p className="text-sm font-bold text-gray-900">
                    {booking.users?.full_name || "Athlete"}
                  </p>
                  <p className="text-xs text-gray-500">
                    {format(new Date(booking.start_at), "MMM d, h:mm a")} -
                    {format(new Date(booking.end_at), "h:mm a")}
                  </p>
                  <p className="text-xs text-gray-500">{booking.location_address || "Location TBD"}</p>
                </div>
                <div className="flex items-center gap-2">
                  <span
                    className={`text-xs font-bold px-2 py-1 rounded-full ${
                      booking.status === "approved"
                        ? "bg-green-100 text-green-700"
                        : booking.status === "pending"
                        ? "bg-yellow-100 text-yellow-700"
                        : "bg-gray-200 text-gray-600"
                    }`}
                  >
                    {booking.status}
                  </span>
                  {booking.status === "pending" && (
                    <>
                      <button
                        type="button"
                        onClick={() => updateBookingStatus(booking.id, "approved")}
                        className="px-3 py-1 rounded-lg bg-green-600 text-white text-xs font-bold"
                      >
                        Approve
                      </button>
                      <button
                        type="button"
                        onClick={() => updateBookingStatus(booking.id, "rejected")}
                        className="px-3 py-1 rounded-lg bg-red-600 text-white text-xs font-bold"
                      >
                        Reject
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

function AthleteScheduleView({ user }) {
  const [programs, setPrograms] = useState([]);
  const [selectedProgramId, setSelectedProgramId] = useState("");
  const [programLocation, setProgramLocation] = useState("");
  const [availabilityRows, setAvailabilityRows] = useState([]);
  const [exceptionRows, setExceptionRows] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!user?.id) return;
    const loadPrograms = async () => {
      const { data } = await supabase
        .from("user_program_follows")
        .select("program_id, programs(id, name, location_address)")
        .eq("user_id", user.id);

      const followed = (data || []).map((row) => ({
        id: row.program_id,
        name: row.programs?.name,
        location: row.programs?.location_address,
      }));

      if (followed.length === 0) {
        setPrograms([]);
        return;
      }

      const { data: availabilityPrograms, error: availabilityError } = await supabase
        .from("trainer_availability")
        .select("program_id")
        .in("program_id", followed.map((p) => p.id));

      if (availabilityError) {
        console.error("Availability filter error", availabilityError);
        setPrograms(followed);
        return;
      }

      const availableIds = new Set((availabilityPrograms || []).map((row) => row.program_id));
      const formatted = followed.filter((p) => availableIds.has(p.id));
      setPrograms(formatted);
      if (formatted.length > 0) {
        setSelectedProgramId(formatted[0].id);
        setProgramLocation(formatted[0].location || "");
      }
    };

    loadPrograms();
  }, [user]);

  useEffect(() => {
    if (!selectedProgramId) return;
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const { rangeStart, rangeEnd, startStr, endStr } = buildDateRange(60, 7);
        const [availabilityRes, exceptionRes, bookingRes] = await Promise.all([
          supabase
            .from("trainer_availability")
            .select(
              "id, weekday, start_time, end_time, location_address, slot_duration_minutes, requires_approval"
            )
            .eq("program_id", selectedProgramId),
          supabase
            .from("trainer_availability_exceptions")
            .select(
              "id, exception_date, is_unavailable, start_time, end_time, location_address, slot_duration_minutes, requires_approval"
            )
            .eq("program_id", selectedProgramId)
            .gte("exception_date", startStr)
            .lte("exception_date", endStr),
          supabase
            .from("trainer_bookings")
            .select("id, start_at, end_at, status, location_address, requires_approval")
            .eq("program_id", selectedProgramId)
            .gte("start_at", rangeStart.toISOString())
            .lte("start_at", rangeEnd.toISOString()),
        ]);

        if (availabilityRes.error) throw availabilityRes.error;
        if (exceptionRes.error) throw exceptionRes.error;
        if (bookingRes.error) throw bookingRes.error;

        setAvailabilityRows(availabilityRes.data || []);
        setExceptionRows(exceptionRes.data || []);
        setBookings(bookingRes.data || []);
      } catch (err) {
        console.error("Load schedule error", err);
        setError(err.message || "Unable to load schedule.");
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [selectedProgramId]);

  useEffect(() => {
    if (!selectedProgramId) return;
    const slots = generateSlots({
      availabilityRows,
      exceptionRows,
      bookings,
      startDate: new Date(),
      days: 21,
      defaultLocation: programLocation,
    });

    setEvents(
      slots.map((slot) => ({
        ...slot,
        title: slot.kind === "available" ? "Available" : slot.title,
      }))
    );
  }, [availabilityRows, exceptionRows, bookings, programLocation, selectedProgramId]);

  const eventPropGetter = (event) => {
    if (event.kind === "available") {
      return {
        style: {
          backgroundColor: "#2563eb",
          borderRadius: "8px",
          border: "none",
          color: "white",
        },
      };
    }

    const statusColor =
      event.status === "approved"
        ? "#059669"
        : event.status === "pending"
        ? "#f59e0b"
        : "#6b7280";

    return {
      style: {
        backgroundColor: statusColor,
        borderRadius: "8px",
        border: "none",
        color: "white",
      },
    };
  };

  const handleSelectEvent = (event) => {
    if (event.kind !== "available") return;
    setSelectedEvent(event);
    setModalOpen(true);
  };

  const bookSlot = async () => {
    if (!selectedEvent || !user?.id) return;
    try {
      const requiresApproval = selectedEvent.requiresApproval;
      const { error: insertError } = await supabase
        .from("trainer_bookings")
        .insert({
          program_id: selectedProgramId,
          athlete_id: user.id,
          start_at: selectedEvent.start,
          end_at: selectedEvent.end,
          status: requiresApproval ? "pending" : "approved",
          requires_approval: requiresApproval,
          location_address: selectedEvent.location || null,
        });
      if (insertError) throw insertError;

      const { rangeStart, rangeEnd } = buildDateRange(60, 7);
      const { data: bookingRes } = await supabase
        .from("trainer_bookings")
        .select("id, start_at, end_at, status, location_address, requires_approval")
        .eq("program_id", selectedProgramId)
        .gte("start_at", rangeStart.toISOString())
        .lte("start_at", rangeEnd.toISOString());
      setBookings(bookingRes || []);
      setModalOpen(false);
    } catch (err) {
      console.error("Booking error", err);
      setError(err.message || "Unable to book slot.");
    }
  };

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-xl flex items-center gap-2">
          <AlertCircle size={16} />
          <span className="text-sm font-bold">{error}</span>
        </div>
      )}

      <div className="bg-white p-6 rounded-3xl shadow-sm border border-gray-100">
        <div className="flex items-center gap-2 mb-4">
          <CalendarIcon className="text-blue-500" size={20} />
          <h2 className="text-lg font-bold text-gray-900">Book a Session</h2>
        </div>
        <div className="flex flex-col md:flex-row md:items-center gap-3">
          <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Trainer</label>
          <select
            className="w-full md:w-80 bg-gray-50 border border-gray-200 text-gray-900 text-sm rounded-xl py-3 px-3 shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none font-medium"
            value={selectedProgramId}
            onChange={(e) => {
              const nextId = e.target.value;
              const nextProgram = programs.find((p) => p.id === nextId);
              setSelectedProgramId(nextId);
              setProgramLocation(nextProgram?.location || "");
            }}
          >
            {programs.map((program) => (
              <option key={program.id} value={program.id}>
                {program.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow p-4 border h-[600px] md:h-[720px]">
        {loading ? (
          <div className="text-sm text-gray-500">Loading schedule...</div>
        ) : (
          <Calendar
            localizer={localizer}
            events={events}
            startAccessor="start"
            endAccessor="end"
            views={["month", "week", "day"]}
            selectable={false}
            onSelectEvent={handleSelectEvent}
            eventPropGetter={eventPropGetter}
          />
        )}
      </div>

      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)}>
        {selectedEvent && (
          <div className="space-y-4">
            <h3 className="text-lg font-bold text-gray-900">Confirm Booking</h3>
            <div className="space-y-2 text-sm text-gray-600">
              <div className="flex items-center gap-2">
                <Clock size={16} />
                <span>{format(selectedEvent.start, "MMM d, h:mm a")}</span>
              </div>
              <div className="flex items-center gap-2">
                <MapPin size={16} />
                <span>{selectedEvent.location || "Location TBD"}</span>
              </div>
            </div>
            <button
              type="button"
              onClick={bookSlot}
              className="w-full bg-blue-600 text-white font-bold py-3 rounded-xl hover:bg-blue-700"
            >
              {selectedEvent.requiresApproval ? "Request Slot" : "Confirm Slot"}
            </button>
          </div>
        )}
      </Modal>
    </div>
  );
}

export default function SchedulePage() {
  const [user, setUser] = useState(null);
  const [role, setRole] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setLoading(false);
        return;
      }
      setUser(user);

      const { data } = await supabase
        .from("users")
        .select("role")
        .eq("id", user.id)
        .single();
      setRole(data?.role || null);
      setLoading(false);
    };

    load();
  }, []);

  if (loading) {
    return <div className="p-6 text-sm text-gray-500">Loading schedule...</div>;
  }

  if (!user || !role) {
    return <div className="p-6 text-sm text-gray-500">Unable to load schedule.</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      <div className="max-w-5xl mx-auto space-y-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-extrabold text-gray-900">Schedule</h1>
          <p className="text-sm text-gray-500 mt-1">
            {role === "SkillsTrainer"
              ? "Set your availability and manage booking requests."
              : "Book a session with your trainer."}
          </p>
        </div>

          {role === "SkillsTrainer" || role === "Administrator" ? (
            <TrainerScheduleView user={user} />
          ) : (
            <AthleteScheduleView user={user} />
          )}
      </div>
    </div>
  );
}
